import React, { useState, useMemo, useCallback } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard, useAnimateOnMount } from '../../common/AnimationUtils';
import { Shield, BarChart2 } from 'lucide-react';
import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Grid3X3,
  Table,
  Info,
  ArrowUpDown,
  Users,
  Activity,
  ClipboardList
} from 'lucide-react';
import HistogramChart from '../charts/HistogramChart';
import DistributionHistogram from '../charts/DistributionHistogram';
import ScoreCard from '../charts/ScoreCard';
import OutlierHeatmap from '../charts/OutlierHeatmap';
import DetailedIssueTooltip from './DetailedIssueTooltip';  // NEW: Hover tooltip for row details
import IssueLogPanel from './IssueLogPanel';  // Inline issue log tab
import PulseBeacon from './PulseBeacon';  // Animated issue indicator
import CollapsibleSection from './CollapsibleSection';
import EmptyStateCelebration from './EmptyStateCelebration';  // NEW: Time-series trend visualization
import './OutliersSection.css'; // Reuse same CSS

// Hoisted to module scope: defining this inside the section component gave it a new
// identity on every re-render, which remounted every card and restarted the load animation.
const MetricCard = ({ colors, isDarkMode, icon: Icon, title, value, subtitle, color, borderColor, severity, animDelay = 0 }) => {
  const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
  const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
  const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');

  return (
    <AnimatedCard delay={animDelay} translateY={12}>
    <div
      className="nsx-metric-card"
      style={{
        position: 'relative',
        padding: '18px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
        overflow: 'hidden',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}
    >
      {severity && (
        <svg
          width="30" height="30" viewBox="0 0 30 30"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={iconColor} strokeWidth="2" fill="none"
            strokeLinecap="round" opacity={0.9}
          />
        </svg>
      )}

      <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '10px',
          }}>
            {title}
          </div>
          <div
            className="metric-value"
            style={{
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colors.text,
              lineHeight: 1.05,
              marginBottom: '6px',
            }}
          >
            <AnimatedNumber value={value} duration={800} />
          </div>
          <div style={{
            fontSize: '11.5px',
            fontWeight: 400,
            letterSpacing: '-0.005em',
            color: colors.textMuted,
            lineHeight: 1.4,
          }}>
            {subtitle}
          </div>
        </div>
        <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
    </AnimatedCard>
  );
};

const OutliersSection = ({ data, dataCollectorId, isSidePanelExpanded, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('summary');
  
  // Collector Analysis view toggle (heatmap is default)
  const [collectorView, setCollectorView] = useState('heatmap');
  
  // Variable Analysis state
  const [variableSearch, setVariableSearch] = useState('');
  const [variableFilter, setVariableFilter] = useState('all');
  const [variableSortField, setVariableSortField] = useState('outlierPercentage');
  const [variableSortDirection, setVariableSortDirection] = useState('desc');
  const [expandedVariable, setExpandedVariable] = useState(null);
  
  // Collector Table state
  const [collectorSortField, setCollectorSortField] = useState('outlierPercentage');
  const [collectorSortDirection, setCollectorSortDirection] = useState('desc');

  // Distribution histogram (Summary Dashboard) selected variable
  const [distVar, setDistVar] = useState(null);

  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    critical: '#DC2626',
    accent: isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  // Early return if no data
  if (!data?.overall) {
    return (
      <div className="uniqueness-empty-state" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px'
      }}>
        <div className="nsx-metric-card" style={{
          textAlign: 'center',
          padding: '32px',
          borderRadius: '16px',
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <AlertTriangle size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Outliers Data Available
          </h3>
          <p style={{ fontSize: '13px', color: colors.textSecondary }}>
            Run an outliers check on numeric variables to see the analysis here.
          </p>
        </div>
      </div>
    );
  }

  // Get variables list (filter out errors and internal metadata keys like _all_detailed_issues, _xlsform_integrated)
  const variables = Object.keys(data.overall).filter(
    variable => !data.overall[variable].error && !variable.startsWith('_')
  );

  // Rating thresholds for outliers
  // Clean: 0%, Low: <2%, Moderate: <5%, High: <10%, Critical: >=10%
  const getStatusInfo = (outlierPercentage) => {
    if (outlierPercentage === 0) return { label: 'Clean', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' };
    if (outlierPercentage < 2) return { label: 'Low', color: '#34D399', bgColor: 'rgba(52, 211, 153, 0.1)' };
    if (outlierPercentage < 5) return { label: 'Moderate', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' };
    if (outlierPercentage < 10) return { label: 'High', color: '#F97316', bgColor: 'rgba(249, 115, 22, 0.1)' };
    return { label: 'Critical', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
  };

  // Calculate overall statistics
  const overallStats = useMemo(() => {
    const totalVariables = variables.length;
    const totalObservations = variables.length > 0 
      ? (data.overall[variables[0]]?.total_valid_observations || 0)
      : 0;
    
    const totalOutliers = variables.reduce((sum, variable) => 
      sum + (data.overall[variable]?.outlier_count || 0), 0
    );
    
    const avgOutlierRate = totalVariables > 0 
      ? variables.reduce((sum, variable) => 
          sum + (data.overall[variable]?.outlier_percentage || 0), 0
        ) / totalVariables
      : 0;
    
    const variablesWithOutliers = variables.filter(variable => 
      (data.overall[variable]?.outlier_count || 0) > 0
    ).length;
    
    const criticalVariables = variables.filter(variable => 
      (data.overall[variable]?.outlier_percentage || 0) >= 10
    ).length;

    const cleanVariables = variables.filter(variable => 
      (data.overall[variable]?.outlier_percentage || 0) === 0
    ).length;

    const lowVariables = variables.filter(variable => {
      const pct = data.overall[variable]?.outlier_percentage || 0;
      return pct > 0 && pct < 2;
    }).length;

    const moderateVariables = variables.filter(variable => {
      const pct = data.overall[variable]?.outlier_percentage || 0;
      return pct >= 2 && pct < 5;
    }).length;

    const highVariables = variables.filter(variable => {
      const pct = data.overall[variable]?.outlier_percentage || 0;
      return pct >= 5 && pct < 10;
    }).length;

    // Data Quality Score: combines average outlier rate with proportion of affected variables
    // Outlier rates are naturally small (<1%), so we also penalize when many variables are affected
    const affectedRatio = totalVariables > 0 ? variablesWithOutliers / totalVariables : 0;
    const qualityScore = Math.max(0, Math.min(100, 
      100 - avgOutlierRate - (affectedRatio * 10) - (criticalVariables * 5)
    ));

    // NEW: XLSForm constraint detection stats
    const xlsformIntegrated = data.overall?._xlsform_integrated || false;
    let varsWithConstraint = 0;
    let varsWithStatistical = 0;
    let constraintOutliers = 0;
    let statisticalOutliers = 0;
    
    if (xlsformIntegrated) {
      variables.forEach(variable => {
        const varData = data.overall[variable];
        if (varData?.detection_method === 'constraint') {
          varsWithConstraint++;
          constraintOutliers += varData.outlier_count || 0;
        } else if (varData?.detection_method === 'statistical' || varData?.detection_method === 'iqr' || varData?.detection_method === 'zscore') {
          varsWithStatistical++;
          statisticalOutliers += varData.outlier_count || 0;
        }
      });
    }

    return {
      totalVariables,
      totalObservations,
      totalOutliers,
      avgOutlierRate,
      variablesWithOutliers,
      criticalVariables,
      cleanVariables,
      lowVariables,
      moderateVariables,
      highVariables,
      qualityScore,
      
      // NEW: XLSForm constraint stats
      xlsformIntegrated,
      constraintStats: xlsformIntegrated ? {
        varsWithConstraint,
        varsWithStatistical,
        constraintOutliers,
        statisticalOutliers
      } : null
    };
  }, [data, variables]);

  // NEW: Aggregate all detailed issues from all variables for tooltip
  const allDetailedIssues = useMemo(() => {
    const issues = [];
    
    // Check for aggregated _all_detailed_issues from backend
    if (data.overall?._all_detailed_issues?.length > 0) {
      return data.overall._all_detailed_issues;
    }
    
    // Otherwise, collect from each variable
    variables.forEach(variable => {
      const varData = data.overall[variable];
      if (varData?.detailed_issues?.length > 0) {
        issues.push(...varData.detailed_issues);
      } else if (varData?.outlier_indices?.length > 0) {
        // Fallback: create issues from outlier_indices
        varData.outlier_indices.forEach((rowIdx, i) => {
          issues.push({
            row_index: rowIdx,
            variable: variable,
            value: varData.outlier_values?.[i],
            respondent_id: null,
            collector_id: null,
            interview_date: null
          });
        });
      }
    });
    
    return issues;
  }, [data, variables]);

  // Get quality level based on score
  const getQualityLevel = (score) => {
    if (score >= 98) return { label: 'Excellent', color: '#10B981', bgColor: 'rgba(16, 185, 129, 0.1)' };
    if (score >= 95) return { label: 'Good', color: '#34D399', bgColor: 'rgba(52, 211, 153, 0.1)' };
    if (score >= 90) return { label: 'Moderate', color: '#F59E0B', bgColor: 'rgba(245, 158, 11, 0.1)' };
    if (score >= 85) return { label: 'Poor', color: '#F97316', bgColor: 'rgba(249, 115, 22, 0.1)' };
    return { label: 'Critical', color: '#EF4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
  };

  // Prepare variable data for table
  const variableTableData = useMemo(() => {
    return variables.map(variable => {
      const varData = data.overall[variable];
      const outlierCount = varData?.outlier_count || 0;
      const outlierPercentage = varData?.outlier_percentage || 0;
      const totalObservations = varData?.total_valid_observations || 0;
      const status = getStatusInfo(outlierPercentage);
      
      return {
        variable,
        outlierCount,
        outlierPercentage,
        totalObservations,
        mean: varData?.mean,
        std: varData?.std,
        min: varData?.min,
        max: varData?.max,
        median: varData?.median,
        status: status.label,
        statusColor: status.color,
        statusBgColor: status.bgColor,
        cleanRate: 100 - outlierPercentage
      };
    });
  }, [data.overall, variables]);

  // Filter and sort variable data
  const filteredVariableData = useMemo(() => {
    let filtered = [...variableTableData];
    
    // Apply search
    if (variableSearch) {
      const searchLower = variableSearch.toLowerCase();
      filtered = filtered.filter(v => v.variable.toLowerCase().includes(searchLower));
    }
    
    // Apply filter
    if (variableFilter !== 'all') {
      filtered = filtered.filter(v => {
        switch (variableFilter) {
          case 'clean': return v.outlierPercentage === 0;
          case 'low': return v.outlierPercentage > 0 && v.outlierPercentage < 2;
          case 'moderate': return v.outlierPercentage >= 2 && v.outlierPercentage < 5;
          case 'high': return v.outlierPercentage >= 5 && v.outlierPercentage < 10;
          case 'critical': return v.outlierPercentage >= 10;
          case 'issues': return v.outlierPercentage > 0;
          default: return true;
        }
      });
    }
    
    // Apply sort
    filtered.sort((a, b) => {
      let aVal = a[variableSortField];
      let bVal = b[variableSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (variableSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return filtered;
  }, [variableTableData, variableSearch, variableFilter, variableSortField, variableSortDirection]);

  // Prepare collector data for table
  const collectorTableData = useMemo(() => {
    if (!data.by_collector) return [];
    
    return Object.entries(data.by_collector).map(([collector, collectorData]) => {
      const totalOutliers = Object.values(collectorData).reduce((sum, varData) => 
        sum + (varData?.outlier_count || 0), 0
      );
      const totalObservations = Object.values(collectorData)[0]?.total_valid_observations || 0;
      
      // Calculate average outlier percentage across all variables
      const varPercentages = Object.values(collectorData)
        .map(v => v?.outlier_percentage || 0)
        .filter(p => !isNaN(p));
      
      const outlierPercentage = varPercentages.length > 0
        ? varPercentages.reduce((sum, pct) => sum + pct, 0) / varPercentages.length
        : 0;
      
      // Count variables with outliers for this collector
      const variablesWithOutliers = Object.values(collectorData)
        .filter(v => (v?.outlier_count || 0) > 0).length;

      const status = getStatusInfo(outlierPercentage);

      return {
        collector,
        outlierPercentage,
        totalOutliers,
        totalObservations,
        variablesWithOutliers,
        totalVariables: variables.length,
        cleanRate: 100 - outlierPercentage,
        status: status.label,
        statusColor: status.color
      };
    });
  }, [data.by_collector, variables]);

  // Sort collector data
  const sortedCollectorData = useMemo(() => {
    const sorted = [...collectorTableData];
    sorted.sort((a, b) => {
      let aVal = a[collectorSortField];
      let bVal = b[collectorSortField];
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (collectorSortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    return sorted;
  }, [collectorTableData, collectorSortField, collectorSortDirection]);

  // Get heatmap data
  const heatmapData = useMemo(() => {
    if (!data.by_collector || !dataCollectorId) return [];
    
    return Object.entries(data.by_collector).flatMap(([collector, collectorData]) =>
      variables.map(variable => ({
        collector,
        variable,
        outlierCount: collectorData[variable]?.outlier_count || 0,
        outlierPercentage: collectorData[variable]?.outlier_percentage || 0,
        totalCount: collectorData[variable]?.total_valid_observations || 0
      }))
    );
  }, [data.by_collector, dataCollectorId, variables]);

  // Export to CSV
  const exportToCSV = useCallback((type) => {
    let csv = '';
    let filename = '';
    
    if (type === 'variables') {
      csv = 'Variable,Outlier Count,Outlier Rate (%),Total Observations,Mean,Std Dev,Min,Max,Clean Rate (%),Status\n';
      filteredVariableData.forEach(v => {
        csv += `${v.variable},${v.outlierCount},${v.outlierPercentage.toFixed(2)},${v.totalObservations},${v.mean?.toFixed(2) || 'N/A'},${v.std?.toFixed(2) || 'N/A'},${v.min?.toFixed(2) || 'N/A'},${v.max?.toFixed(2) || 'N/A'},${v.cleanRate.toFixed(2)},${v.status}\n`;
      });
      filename = 'outliers_variable_analysis.csv';
    } else if (type === 'collectors') {
      csv = 'Collector,Outlier Rate (%),Total Outliers,Variables Affected,Clean Rate (%),Status\n';
      sortedCollectorData.forEach(c => {
        csv += `${c.collector},${c.outlierPercentage.toFixed(2)},${c.totalOutliers},${c.variablesWithOutliers}/${c.totalVariables},${c.cleanRate.toFixed(2)},${c.status}\n`;
      });
      filename = 'outliers_collector_analysis.csv';
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [filteredVariableData, sortedCollectorData]);

  // Toggle sort
  const toggleSort = (field, type) => {
    if (type === 'variable') {
      if (variableSortField === field) {
        setVariableSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setVariableSortField(field);
        setVariableSortDirection('desc');
      }
    } else {
      if (collectorSortField === field) {
        setCollectorSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setCollectorSortField(field);
        setCollectorSortDirection('desc');
      }
    }
  };

  // Sortable header component
  const SortableHeader = ({ field, label, type, align = 'left' }) => {
    const isActive = type === 'variable' ? variableSortField === field : collectorSortField === field;
    const direction = type === 'variable' ? variableSortDirection : collectorSortDirection;
    
    return (
      <th 
        onClick={() => toggleSort(field, type)}
        style={{ 
          padding: '14px 16px', 
          textAlign: align, 
          fontSize: '14px', 
          fontWeight: '450',
          color: isActive ? colors.accent : colors.textSecondary,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          borderBottom: `2px solid ${colors.border}`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          {label}
          <ArrowUpDown size={14} style={{ opacity: isActive ? 1 : 0.4 }} />
          {isActive && (
            direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          )}
        </div>
      </th>
    );
  };

  // Metric card component (matching Uniqueness style)

  const qualityLevel = getQualityLevel(overallStats.qualityScore);

  // Clean Rate Bar with grow-from-zero animation
  const CleanRateBar = ({ rate, color }) => {
    const barReady = useAnimateOnMount(400);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          flex: 1,
          height: '8px',
          backgroundColor: isDarkMode ? '#2A2A2E' : '#929292ff',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: barReady ? `${rate}%` : '0%',
            height: '100%',
            backgroundColor: color,
            borderRadius: '4px',
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }} />
        </div>
        <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text, minWidth: '50px' }}>
          {barReady ? <AnimatedNumber value={rate} decimals={1} suffix="%" delay={0} duration={800} /> : '0.0%'}
        </span>
      </div>
    );
  };

  // ============== RENDER SECTIONS ==============

  // Summary Dashboard Tab
  const renderSummaryDashboard = () => (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'minmax(0, 1fr)',
      width: '100%',
      gap: '16px',
      marginTop: '80px'
    }}>
      

      {/* XLSForm Constraint Banner - Shows when constraints are used */}
      {overallStats.xlsformIntegrated && overallStats.constraintStats?.varsWithConstraint > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '12px 16px',
          backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
          border: isDarkMode ? '1px solid rgba(16, 185, 129, 0.3)' : '0px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '12px'
        }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Shield size={22} style={{ color: '#10B981' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#10B981' }}>
                XLSForm Constraints Applied
              </span>
            </div>
            <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: 0, lineHeight: '1.5' }}>
              <strong style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>
                {overallStats.constraintStats.varsWithConstraint}
              </strong> variable{overallStats.constraintStats.varsWithConstraint !== 1 ? 's' : ''} use form-defined bounds.
              {overallStats.constraintStats.varsWithStatistical > 0 && (
                <span>
                  {' '}<strong style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>
                    {overallStats.constraintStats.varsWithStatistical}
                  </strong> use statistical fallback.
                </span>
              )}
            </p>
          </div>
          <div style={{
            display: 'flex',
            gap: '16px',
            paddingLeft: '20px',
            borderLeft: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}`
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: '700', color: '#10B981' }}>
                <Shield size={16} />
                {overallStats.constraintStats.varsWithConstraint}
              </div>
              <div style={{ fontSize: '10px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Constraint</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: '700', color: '#3B82F6' }}>
                <BarChart2 size={16} />
                {overallStats.constraintStats.varsWithStatistical}
              </div>
              <div style={{ fontSize: '10px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Statistical</div>
            </div>
          </div>

          

        </div>
      )}

      {/* XLSForm Loaded But No Constraints Applied Banner - Transparency indicator */}
      {overallStats.xlsformIntegrated && 
       (!overallStats.constraintStats?.varsWithConstraint || overallStats.constraintStats.varsWithConstraint === 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 20px',
          backgroundColor: isDarkMode ? 'rgba(107, 114, 128, 0.1)' : 'rgba(107, 114, 128, 0.08)',
          border: `0px solid ${isDarkMode ? 'rgba(107, 114, 128, 0.3)' : 'rgba(107, 114, 128, 0.2)'}`,
          borderRadius: '12px'
        }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px',
            backgroundColor: isDarkMode ? 'rgba(107, 114, 128, 0.2)' : 'rgba(107, 114, 128, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <Info size={20} style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
                XLSForm Loaded - Statistical Analysis Used
              </span>
            </div>
            <p style={{ fontSize: '12px', color: isDarkMode ? '#6B7280' : '#9CA3AF', margin: 0, lineHeight: '1.4' }}>
              None of the analyzed numeric variables have constraint bounds defined in the XLSForm. 
              Using statistical (Z-score/IQR) outlier detection instead.
            </p>
          </div>
          <div style={{ 
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '6px 12px', 
            backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
            borderRadius: '8px',
            fontSize: '11px',
            color: isDarkMode ? '#93C5FD' : '#3B82F6',
            fontWeight: '500'
          }}>
            <BarChart2 size={14} />
            {overallStats.constraintStats?.varsWithStatistical || overallStats.totalVariables} Statistical
          </div>
        </div>
      )}

      <CollapsibleSection 
        title="Key Metrics" 
        isDarkMode={isDarkMode} 
        colors={colors}
        badge={
          overallStats.criticalVariables > 0 ? (
            <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
              {overallStats.criticalVariables} critical
            </span>
          ) : null
        }
      >
      {/* Top Metrics Row */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px' 
      }}>
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          animDelay={0}
          icon={BarChart3}
          title="Variables Analyzed"
          value={overallStats.totalVariables}
          subtitle="Checked for outliers"
          color="#6B7280"
          borderColor="#6B7280"
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          animDelay={80}
          icon={AlertTriangle}
          title="Variables with Outliers"
          value={overallStats.variablesWithOutliers}
          subtitle="Have outlier points"
          color={overallStats.variablesWithOutliers > 0 ? colors.warning : colors.success}
          borderColor={overallStats.variablesWithOutliers > 0 ? colors.warning : colors.success}
          severity={overallStats.variablesWithOutliers > 0 ? 'warning' : null}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          animDelay={160}
          icon={XCircle}
          title="Critical Variables"
          value={overallStats.criticalVariables}
          subtitle="≥10% outlier rate"
          color={overallStats.criticalVariables > 0 ? colors.error : colors.success}
          borderColor={overallStats.criticalVariables > 0 ? colors.error : colors.success}
          severity={overallStats.criticalVariables > 0 ? 'critical' : null}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          animDelay={240}
          icon={Activity}
          title="Avg Outlier Rate"
          value={`${overallStats.avgOutlierRate.toFixed(1)}%`}
          subtitle="Across all variables"
          color={getStatusInfo(overallStats.avgOutlierRate).color}
          borderColor={getStatusInfo(overallStats.avgOutlierRate).color}
          severity={overallStats.avgOutlierRate >= 10 ? 'critical' : overallStats.avgOutlierRate >= 3 ? 'warning' : null}
        />
      </div>
      </CollapsibleSection>

      <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
      {/* Compact Analysis Row: Score + Breakdown + Top Issues */}
      <div className="analysis-grid-4col" style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))', 
        gap: '16px' 
      }}>
        {/* Quality Score */}
        <AnimatedCard delay={320} translateY={12}>
          <ScoreCard
            eyebrow="data quality score"
            score={overallStats.qualityScore}
            label={qualityLevel.label}
            caption={overallStats.qualityScore >= 98 ? 'Excellent quality' : overallStats.qualityScore >= 95 ? 'Minor outliers' : overallStats.qualityScore >= 90 ? 'Review high-rate vars' : 'Review recommended'}
            isDarkMode={isDarkMode}
            startDelay={620}
          />
        </AnimatedCard>

        {/* Variable Breakdown */}
        <AnimatedCard delay={400} translateY={12}>
        <div className="nsx-metric-card" style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          <div style={{
            fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em',
            textTransform: 'lowercase', color: colors.textMuted, marginBottom: '14px',
          }}>
            variable breakdown
          </div>
          <div>
            {[
              { label: 'Clean (0%)',        value: overallStats.cleanVariables,    tone: 'success' },
              { label: 'Low (<2%)',         value: overallStats.lowVariables,      tone: 'success2' },
              { label: 'Moderate (2-5%)',   value: overallStats.moderateVariables, tone: 'warning' },
              { label: 'High (5-10%)',      value: overallStats.highVariables,     tone: 'russet' },
              { label: 'Critical (≥10%)',   value: overallStats.criticalVariables, tone: 'error' },
            ].map((item, idx) => {
              const c = item.tone === 'success'  ? (isDarkMode ? '#34d399' : '#047857')
                      : item.tone === 'success2' ? (isDarkMode ? '#10b981' : '#059669')
                      : item.tone === 'warning'  ? (isDarkMode ? '#fbbf24' : '#b45309')
                      : item.tone === 'russet'   ? '#D97706'
                      : (isDarkMode ? '#f87171' : '#b91c1c');
              return (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '7px 0',
                  borderBottom: idx < 4 ? `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <div style={{
                      width: '10px', height: '10px', borderRadius: '3px',
                      background: c,
                      boxShadow: `0 0 6px ${c}33`,
                    }} />
                    <span style={{ color: colors.text, fontSize: '12px', letterSpacing: '-0.005em' }}>{item.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, letterSpacing: '-0.01em', color: c }}>{item.value}</span>
                    <span style={{ fontSize: '11px', color: colors.textMuted }}>/ {overallStats.totalVariables}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        </AnimatedCard>

        {/* Top Variable Issues */}
        <AnimatedCard delay={480} translateY={12}>
        <div className="nsx-metric-card" style={{
          position: 'relative',
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden',
          fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'lowercase', color: colors.textMuted, marginBottom: '3px' }}>top issues</div>
              <div style={{ fontSize: '13.5px', fontWeight: 500, letterSpacing: '-0.01em', color: colors.text }}>Top Variable Issues</div>
            </div>
            <span style={{ fontSize: '9.5px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted, paddingTop: '14px' }}>top 5</span>
          </div>
          <div style={{ overflowX: 'auto', margin: '0 -4px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
              <thead>
                <tr>
                  {['variable','rate','status'].map(h => (
                    <th key={h} style={{
                      padding: '6px 10px 8px',
                      textAlign: h === 'variable' ? 'left' : 'right',
                      color: colors.textMuted, fontWeight: 500, fontSize: '9.5px',
                      letterSpacing: '0.06em', textTransform: 'lowercase',
                      borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {variableTableData.filter(v => v.outlierPercentage > 0).sort((a, b) => b.outlierPercentage - a.outlierPercentage).slice(0, 5).map(v => {
                  const rate = v.outlierPercentage;
                  const c = rate >= 10 ? (isDarkMode ? '#f87171' : '#b91c1c')
                         : rate >= 2 ? (isDarkMode ? '#fbbf24' : '#b45309')
                         : (isDarkMode ? '#34d399' : '#047857');
                  const status = getStatusInfo(rate);
                  return (
                    <tr key={v.variable}>
                      <td style={{ padding: '9px 10px', color: colors.text, fontWeight: 400, letterSpacing: '-0.005em', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.variable}>{v.variable}</td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        <span style={{ color: c, fontWeight: 500, fontSize: '13px' }}>{rate.toFixed(1)}%</span>
                      </td>
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: '2px 9px', borderRadius: '100px',
                          fontSize: '10px', fontWeight: 500,
                          letterSpacing: '0.04em', textTransform: 'lowercase',
                          background: isDarkMode ? `${c}22` : `${c}14`,
                          color: c, border: `0.5px solid ${c}33`,
                        }}>{status.label}</span>
                      </td>
                    </tr>
                  );
                })}
                {variableTableData.filter(v => v.outlierPercentage > 0).length === 0 && (
                  <tr><td colSpan={3} style={{ padding: '22px', textAlign: 'center', color: colors.textMuted, fontSize: '11.5px' }}>No outliers detected</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </AnimatedCard>

        {/* Top Collector Issues */}
        <AnimatedCard delay={560} translateY={12}>
        {dataCollectorId && collectorTableData.length > 0 ? (
          <div className="nsx-metric-card" style={{
            position: 'relative',
            padding: '18px',
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
            borderRadius: '8px',
            border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            overflow: 'hidden',
            fontFamily: "'Outfit', -apple-system, sans-serif",
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'lowercase', color: colors.textMuted, marginBottom: '3px' }}>top issues</div>
                <div style={{ fontSize: '13.5px', fontWeight: 500, letterSpacing: '-0.01em', color: colors.text }}>Top Collector Issues</div>
              </div>
              <span style={{ fontSize: '9.5px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted, paddingTop: '14px' }}>top 5</span>
            </div>
            <div style={{ overflowX: 'auto', margin: '0 -4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                <thead>
                  <tr>
                    {['collector','rate','status'].map(h => (
                      <th key={h} style={{
                        padding: '6px 10px 8px',
                        textAlign: h === 'collector' ? 'left' : 'right',
                        color: colors.textMuted, fontWeight: 500, fontSize: '9.5px',
                        letterSpacing: '0.06em', textTransform: 'lowercase',
                        borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...collectorTableData].sort((a, b) => b.outlierPercentage - a.outlierPercentage).filter(c => c.outlierPercentage > 0).slice(0, 5).map(c => {
                    const rate = c.outlierPercentage;
                    const toneC = rate >= 10 ? (isDarkMode ? '#f87171' : '#b91c1c')
                               : rate >= 2 ? (isDarkMode ? '#fbbf24' : '#b45309')
                               : (isDarkMode ? '#34d399' : '#047857');
                    const status = getStatusInfo(rate);
                    return (
                      <tr key={c.collector}>
                        <td style={{ padding: '9px 10px', color: colors.text, fontWeight: 400, letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '150px' }}>{c.collector}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          <span style={{ color: toneC, fontWeight: 500, fontSize: '13px' }}>{rate.toFixed(1)}%</span>
                        </td>
                        <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            padding: '2px 9px', borderRadius: '100px',
                            fontSize: '10px', fontWeight: 500,
                            letterSpacing: '0.04em', textTransform: 'lowercase',
                            background: isDarkMode ? `${toneC}22` : `${toneC}14`,
                            color: toneC, border: `0.5px solid ${toneC}33`,
                          }}>{status.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="nsx-metric-card" style={{ backgroundColor: colors.cardBg, borderRadius: '12px', border: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px', color: colors.textMuted, fontSize: '13px', textAlign: 'center' }}>
            No collector ID specified
          </div>
        )}
        </AnimatedCard>
      </div>

      <style>{`
        @media (max-width: 1200px) { .analysis-grid-4col { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 768px) { .analysis-grid-4col { grid-template-columns: 1fr !important; } }
      `}</style>
      </CollapsibleSection>

      {/* Distribution histogram for the selected variable */}
      {(() => {
        const candidates = variables.filter(v => data.overall[v]?.histogram?.counts?.length);
        if (candidates.length === 0) return null;
        const ranked = [...candidates].sort((a, b) =>
          (data.overall[b]?.outlier_percentage || 0) - (data.overall[a]?.outlier_percentage || 0)
        );
        const selected = distVar && candidates.includes(distVar) ? distVar : ranked[0];
        const vd = data.overall[selected] || {};

        // Resolve acceptable bounds from constraint or statistical detection
        const pick = (obj, keys) => {
          if (!obj) return null;
          for (const k of keys) if (typeof obj[k] === 'number') return obj[k];
          return null;
        };
        let lower = null, upper = null, methodLabel = 'statistical';
        if (vd.detection_method === 'constraint' && vd.constraint_bounds) {
          lower = pick(vd.constraint_bounds, ['min', 'lower', 'lower_bound', 'min_value']);
          upper = pick(vd.constraint_bounds, ['max', 'upper', 'upper_bound', 'max_value']);
          methodLabel = 'form constraint';
        } else {
          const sb = vd.statistical_bounds || {};
          lower = pick(sb, ['lower', 'lower_bound', 'min']);
          upper = pick(sb, ['upper', 'upper_bound', 'max']);
          if (lower === null && upper === null && typeof vd.mean === 'number' && typeof vd.std === 'number') {
            const t = typeof sb.threshold === 'number' ? sb.threshold : 3;
            lower = vd.mean - t * vd.std;
            upper = vd.mean + t * vd.std;
            methodLabel = `z-score (\u00b1${t}\u03c3)`;
          } else if (sb.method) {
            methodLabel = String(sb.method).toUpperCase();
          }
        }

        const rate = vd.outlier_percentage || 0;
        const count = vd.outlier_count || 0;
        const tone = rate >= 10 ? 'critical' : rate >= 2 ? 'warning' : count > 0 ? 'neutral' : 'success';
        const fmtB = (x) => (x === null ? 'n/a' : Math.abs(x) >= 100 ? x.toFixed(0) : x.toFixed(1).replace(/\.0$/, ''));
        const headline = count === 0
          ? `No outliers in ${selected}.`
          : `${rate.toFixed(1)}% of ${selected} falls outside ${fmtB(lower)} to ${fmtB(upper)}.`;
        const detail = count === 0
          ? `All ${(vd.total_valid_observations || 0).toLocaleString()} values sit inside the ${methodLabel} bounds.`
          : `${count.toLocaleString()} of ${(vd.total_valid_observations || 0).toLocaleString()} values flagged by ${methodLabel}; median ${fmtB(vd.median)}.`;

        return (
          <CollapsibleSection title="Distribution" isDarkMode={isDarkMode} colors={colors}>
            <AnimatedCard delay={640} translateY={12}>
              <DistributionHistogram
                key={selected}
                title={`${selected} distribution`}
                histogram={vd.histogram}
                range={{ min: lower, max: upper }}
                outsideLabel="outside bounds"
                insideLabel={`within ${fmtB(lower)}\u2013${fmtB(upper)}`}
                insight={{ tone, headline, detail }}
                isDarkMode={isDarkMode}
                isMobile={isMobile}
                startDelay={950}
                headerRight={candidates.length > 1 ? (
                  <select
                    className="dqh-select"
                    value={selected}
                    onChange={(e) => setDistVar(e.target.value)}
                    aria-label="Choose variable"
                  >
                    {ranked.map(v => (
                      <option key={v} value={v}>
                        {v} ({(data.overall[v]?.outlier_percentage || 0).toFixed(1)}%)
                      </option>
                    ))}
                  </select>
                ) : null}
              />
            </AnimatedCard>
          </CollapsibleSection>
        );
      })()}
    </div>
  );

  // Variable Analysis Tab
  const renderVariableAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* Search, Filter, Export Controls */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{
          flex: '1',
          minWidth: '250px',
          position: 'relative'
        }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted
          }} />
          <input
            type="text"
            placeholder="Search variables..."
            value={variableSearch}
            onChange={(e) => setVariableSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 14px 12px 44px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Filter Dropdown */}
        <div style={{ position: 'relative' }}>
          <Filter size={16} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted
          }} />
          <select
            value={variableFilter}
            onChange={(e) => setVariableFilter(e.target.value)}
            style={{
              padding: '12px 36px 12px 36px',
              borderRadius: '10px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '14px',
              cursor: 'pointer',
              outline: 'none',
              appearance: 'none'
            }}
          >
            <option value="all">All Variables</option>
            <option value="clean">Clean (0%)</option>
            <option value="low">Low (&lt;2%)</option>
            <option value="moderate">Moderate (2-5%)</option>
            <option value="high">High (5-10%)</option>
            <option value="critical">Critical (≥10%)</option>
            <option value="issues">With Issues</option>
          </select>
          <ChevronDown size={16} style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted,
            pointerEvents: 'none'
          }} />
        </div>

        {/* Export Button */}
        <button
          onClick={() => exportToCSV('variables')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            borderRadius: '10px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.cardBg,
            color: colors.text,
            fontSize: '14px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          <Download size={16} />
          Export
        </button>
      </div>
      </AnimatedCard>

      {/* Results count */}
      <AnimatedCard delay={80} translateY={12}>
      <div style={{ marginBottom: '12px', fontSize: '13px', color: colors.textMuted }}>
        Showing {filteredVariableData.length} of {variableTableData.length} variables
      </div>
      </AnimatedCard>

      {/* Variable Table */}
      <AnimatedCard delay={160} translateY={12}>
      <div className="nsx-metric-card" style={{
        backgroundColor: colors.cardBg,
        borderRadius: '16px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: isDarkMode ? '#17171B' : '#F9FAFB' }}>
                <SortableHeader field="variable" label="Variable" type="variable" />
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}`, width: '200px' }}>
                  Clean Rate
                </th>
                <SortableHeader field="outlierPercentage" label="Outlier Rate" type="variable" align="center" />
                <SortableHeader field="outlierCount" label="Outlier Count" type="variable" align="center" />
                <SortableHeader field="totalObservations" label="Observations" type="variable" align="center" />
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}` }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredVariableData.map((variable, index) => (
                <React.Fragment key={variable.variable}>
                  <tr 
                    style={{ 
                      borderBottom: `1px solid ${colors.borderLight}`,
                      backgroundColor: expandedVariable === variable.variable ? (isDarkMode ? '#1F1F23' : '#F9FAFB') : 'transparent',
                      cursor: 'pointer'
                    }}
                    onClick={() => setExpandedVariable(expandedVariable === variable.variable ? null : variable.variable)}
                  >
                    <td style={{ padding: '14px 16px', fontSize: '15px', fontWeight: '500', color: colors.text }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {expandedVariable === variable.variable ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {variable.variable}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <CleanRateBar rate={variable.cleanRate} color={variable.statusColor} />
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: variable.statusColor }}>
                      {variable.outlierPercentage.toFixed(2)}%
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', color: colors.text }}>
                      {variable.outlierCount.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', color: colors.textSecondary }}>
                      {variable.totalObservations.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '5px 12px',
                        borderRadius: '12px',
                        fontSize: '15px',
                        fontWeight: '600',
                        backgroundColor: variable.statusBgColor,
                        color: variable.statusColor
                      }}>
                        {variable.status}
                      </span>
                    </td>
                  </tr>
                  {/* Expanded row with histogram */}
                  {expandedVariable === variable.variable && (
                    <tr>
                      <td colSpan={6} style={{ padding: '0', backgroundColor: isDarkMode ? '#1A1A1E' : '#F5F5F5' }}>
                        <div style={{ padding: isMobile ? '12px' : '24px', display: 'flex', gap: isMobile ? '16px' : '24px', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                          {/* Histogram */}
                          <div style={{ flex: '2', minWidth: isMobile ? '0' : '400px' }}>
                            <HistogramChart
                              data={data.overall}
                              selectedVariable={variable.variable}
                              width={isMobile ? 340 : 500}
                              height={isMobile ? 250 : 300}
                            />
                          </div>
                          {/* Stats panel */}
                          <div style={{ flex: '1', minWidth: isMobile ? '0' : '200px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '450', color: colors.text, marginBottom: '12px' }}>
                              Statistical Summary
                            </div>
                            {[
                              { label: 'Mean', value: variable.mean?.toFixed(2) },
                              { label: 'Std Dev', value: variable.std?.toFixed(2) },
                              { label: 'Median', value: variable.median?.toFixed(2) },
                              { label: 'Min', value: variable.min?.toFixed(2) },
                              { label: 'Max', value: variable.max?.toFixed(2) }
                            ].map(stat => (
                              <div key={stat.label} style={{
                                display: 'flex',
                                justifyContent: isMobile ? 'flex-start' : 'space-between',
                                alignItems: 'center',
                                gap: isMobile ? '12px' : '0',
                                padding: '8px 0',
                                borderBottom: `1px solid ${colors.borderLight}`
                              }}>
                                <span style={{ fontSize: '15px', color: colors.textSecondary, minWidth: isMobile ? '88px' : 'auto' }}>{stat.label}</span>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text, fontVariantNumeric: 'tabular-nums' }}>{stat.value || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredVariableData.length === 0 && (
          <div style={{ padding: '32px', textAlign: 'center', color: colors.textMuted }}>
            No variables match your search criteria
          </div>
        )}
      </div>
      </AnimatedCard>
    </div>
  );

  // Collector Analysis Tab
  const renderCollectorAnalysis = () => (
    <div style={{ marginTop: '80px' }}>
      {/* View Toggle */}
      <AnimatedCard delay={0} translateY={12}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '14px',
        paddingBottom: '10px',
        borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '3px',
          }}>
            collector analysis
          </div>
          <h3 style={{
            fontSize: '15px',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            color: colors.text,
            margin: 0,
          }}>
            Outlier patterns by data collector
          </h3>
        </div>

        <div style={{ display: 'flex', gap: '0', alignItems: 'center' }}>
          {[
            { key: 'heatmap', icon: Grid3X3, label: 'heatmap' },
            { key: 'table',   icon: Table,   label: 'table' },
          ].map(({ key, icon: VIcon, label }) => {
            const isActive = collectorView === key;
            return (
              <button
                key={key}
                onClick={() => setCollectorView(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '0',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: isActive ? colors.text : colors.textMuted,
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'lowercase',
                  cursor: 'pointer',
                  opacity: isActive ? 1 : 0.7,
                  borderBottom: isActive ? '2px solid #D97706' : '2px solid transparent',
                  marginBottom: '-11px',
                  transition: 'all 0.2s ease',
                  userSelect: 'none',
                }}
              >
                <VIcon size={13} strokeWidth={isActive ? 2 : 1.75} />
                {label}
              </button>
            );
          })}
        </div>
      </div>
      </AnimatedCard>

      {/* Export row — shown only for table view, below the toggle */}
      {collectorView === 'table' && (
        <AnimatedCard delay={40} translateY={8}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button
            onClick={() => exportToCSV('collectors')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              padding: '7px 14px',
              borderRadius: '100px',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
              color: colors.text,
              fontSize: '12px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              fontFamily: "'Outfit', -apple-system, sans-serif",
              transition: 'all 0.2s ease',
            }}
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
        </AnimatedCard>
      )}

      {/* Heatmap View */}
      <AnimatedCard delay={80} translateY={12}>
      {collectorView === 'heatmap' && (
        <div className="nsx-metric-card" style={{
          padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}>
          {heatmapData.length > 0 ? (
            <OutlierHeatmap 
              data={heatmapData}
              height={Math.max(400, Object.keys(data.by_collector || {}).length * 50)}
            />
          ) : (
            <div style={{ padding: '32px', textAlign: 'center', color: colors.textMuted }}>
              <AlertTriangle size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
              <div>No collector data available for heatmap visualization</div>
              <div style={{ fontSize: '12px', marginTop: '8px' }}>
                Make sure a Data Collector ID is configured
              </div>
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Table View */}
      <AnimatedCard delay={160} translateY={12}>
      {collectorView === 'table' && (
        <div className="nsx-metric-card" style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: isDarkMode ? '#17171B' : '#F9FAFB' }}>
                  <SortableHeader field="collector" label="Data Collector" type="collector" />
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}`, width: '200px' }}>
                    Clean Rate
                  </th>
                  <SortableHeader field="outlierPercentage" label="Outlier Rate" type="collector" align="center" />
                  <SortableHeader field="totalOutliers" label="Total Outliers" type="collector" align="center" />
                  <SortableHeader field="variablesWithOutliers" label="Variables Affected" type="collector" align="center" />
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}` }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectorData.map((collector) => (
                  <tr key={collector.collector} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '500', color: colors.text }}>
                      {collector.collector}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <CleanRateBar rate={collector.cleanRate} color={collector.statusColor} />
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: collector.statusColor }}>
                      {collector.outlierPercentage.toFixed(2)}%
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', color: colors.text }}>
                      {collector.totalOutliers.toLocaleString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', color: colors.textSecondary }}>
                      {collector.variablesWithOutliers}/{collector.totalVariables}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '5px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: `${collector.statusColor}15`,
                        color: collector.statusColor
                      }}>
                        {collector.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedCollectorData.length === 0 && (
            <div style={{ padding: '32px', textAlign: 'center', color: colors.textMuted }}>
              No collector data available
            </div>
          )}
        </div>
      )}
      </AnimatedCard>

      {/* Rating Thresholds Legend */}
      <AnimatedCard delay={320} translateY={12}>
      <div style={{
        marginTop: '16px',
        padding: '12px 16px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.5)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        backdropFilter: 'blur(12px)',
        display: 'flex',
        alignItems: 'center',
        gap: '18px',
        flexWrap: 'wrap',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Info size={13} style={{ color: colors.textMuted, opacity: 0.7 }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
          }}>
            rating thresholds
          </span>
        </div>
        {[
          { label: 'Clean',    range: '0%',    color: isDarkMode ? '#34d399' : '#047857' },
          { label: 'Low',      range: '<2%',   color: isDarkMode ? '#10b981' : '#059669' },
          { label: 'Moderate', range: '<5%',   color: isDarkMode ? '#fbbf24' : '#b45309' },
          { label: 'High',     range: '<10%',  color: '#D97706' },
          { label: 'Critical', range: '≥10%',  color: isDarkMode ? '#f87171' : '#b91c1c' },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <div style={{
              width: '10px',
              height: '10px',
              borderRadius: '3px',
              backgroundColor: item.color,
              boxShadow: `0 0 6px ${item.color}33`,
            }} />
            <span style={{
              fontSize: '11.5px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              color: colors.text,
            }}>
              {item.label}
            </span>
            <span style={{
              fontSize: '10.5px',
              fontWeight: 400,
              color: colors.textMuted,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {item.range}
            </span>
          </div>
        ))}
      </div>
      </AnimatedCard>
    </div>
  );

  // Tab definitions
  const tabs = [
    { id: 'summary', label: 'Summary Dashboard', icon: BarChart3 },
    { id: 'variables', label: 'Variable Analysis', icon: Table },
    ...(dataCollectorId ? [{ id: 'collectors', label: 'Collector Analysis', icon: Grid3X3 }] : []),
    ...(allDetailedIssues.length > 0 ? [{ id: 'issues', label: 'Issue Log', icon: ClipboardList }] : [])
  ];

  // Tab Navigation Component (matching Uniqueness style)
  // ============== MAIN RENDER ==============

  // XLSForm Constraint Banner Component
  const XLSFormConstraintBanner = () => {
    if (!xlsformIntegrated || varsWithConstraint === 0) return null;
    
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 16px',
        backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.08)',
        border: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
        borderRadius: '12px', marginBottom: '24px'
      }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '10px',
          backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <Shield size={22} style={{ color: '#10B981' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#10B981', marginBottom: '4px' }}>
            XLSForm Constraints Applied
          </div>
          <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: 0 }}>
            <strong style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>{varsWithConstraint}</strong> variable{varsWithConstraint !== 1 ? 's' : ''} use form-defined bounds.
            {varsWithStatistical > 0 && <span> <strong style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>{varsWithStatistical}</strong> use statistical fallback.</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '16px', paddingLeft: '20px',
          borderLeft: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.2)' : 'rgba(16, 185, 129, 0.15)'}` }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: '700', color: '#10B981' }}>
              <Shield size={16} />{varsWithConstraint}
            </div>
            <div style={{ fontSize: '10px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Constraint</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', fontWeight: '700', color: '#3B82F6' }}>
              <BarChart2 size={16} />{varsWithStatistical}
            </div>
            <div style={{ fontSize: '10px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Statistical</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div 
      className="outliers-section activeQualityDetector"
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
        overflow: isMobile ? 'visible' : 'hidden',
      }}
    >
      {/* Dimension Header */}
      <DimensionHeader
        slug="outliers"
        title="Outliers"
        description="Identifies unusual numeric values that fall outside expected ranges or form constraints. Outliers may signal data-entry errors, instrument issues, or genuinely exceptional respondents worth reviewing."
        rememberCollapseKey="outliers"
        stat={{
          score: overallStats.qualityScore,
          scoreLabel: 'quality score',
          unit: '%',
        }}
        tabs={tabs.map(t => {
          if (t.id === 'variables')  return { ...t, count: overallStats.totalVariables, countTone: 'neutral' };
          if (t.id === 'collectors') return { ...t, count: Object.keys(data?.by_collector || {}).length, countTone: 'neutral' };
          if (t.id === 'issues')     return { ...t, count: allDetailedIssues.length,
                                              countTone: allDetailedIssues.length > 0 ? 'alert' : 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />

      {/* Tab Content */}
      <div style={{ 
          minHeight: '600px',
          marginTop: '16px'
        }}>
        {activeTab === 'summary' && renderSummaryDashboard()}
        {activeTab === 'variables' && renderVariableAnalysis()}
        {activeTab === 'collectors' && renderCollectorAnalysis()}

        {activeTab === 'issues' && (
          <AnimatedCard delay={0} translateY={12}>
          <IssueLogPanel
            issues={allDetailedIssues}
            title={`${overallStats.totalOutliers.toLocaleString()} Outliers`}
            issueType="outlier"
            totalCount={overallStats.totalOutliers}
          />
          </AnimatedCard>
        )}
      </div>
    </div>
  );
};

export default OutliersSection;